import { UnitData } from "../../../../to-be-removed/LocalData/Property/Units/UnitData";
import { annualCalculations } from "./CpiEditPopup/CpiEditPopup";
import { IApiUnit } from "@propely/propely-typings";
import { calculateRemainingContractValue, calculateTotalAnnualRent } from "@src/utils/data/economical/economicalMetrics";

export class ExpirationProfileWidgetHelper {
  /**
   * Calculate a expiration profile for a set amount of years
   * Any unit with valid contracts, contracts that end the given year or start given year are taken into the calculation
   * @param years total years in future to make a expiration profile
   * @param withCPI
   * @returns number to map to line chart
   */
  static annualCpiRents = (units: IApiUnit[], annualCPIs: annualCalculations, currentYear: number, years: String, withCPI: boolean): number[] => {
    const numberOfYears: number = +years;
    const chart_numbers: number[] = [];
    for (let i = 0; i < numberOfYears; i++) {
      const filteredUnits: IApiUnit[] = this.getValidUnits(units, currentYear + i, false);

      const cpiPrognosisNumber = ExpirationProfileWidgetHelper.getTotalAnnualRent(filteredUnits, annualCPIs, currentYear, i, withCPI);
      chart_numbers.push(cpiPrognosisNumber);
    }
    return chart_numbers;
  };

  static getValidUnits = (units: IApiUnit[], currentYear: number, getNonValidContracts: boolean): IApiUnit[] => {
    const date = new Date();
    const calculatedYear = new Date(currentYear, date.getMonth(), date.getDay());

    const filteredUnits = units.filter((unit) => {
      if (
        unit.fields?.runningRenewal?.value &&
        (!unit.fields?.contractEndDate?.value || new Date(unit.fields?.contractEndDate.value) > calculatedYear)
      ) {
        return true;
      }

      const startDate = unit.fields?.contractStartDate?.value ? new Date(unit.fields?.contractStartDate?.value) : undefined;
      const endDate = unit.fields?.contractEndDate?.value ? new Date(unit.fields?.contractEndDate?.value) : undefined;

      if (!startDate) {
        return false;
      }

      if (endDate && endDate.getFullYear() < calculatedYear.getFullYear()) {
        return false;
      } else if (startDate.getFullYear() > calculatedYear.getFullYear()) {
        return false;
      } else {
        return true;
      }
    });

    if (getNonValidContracts) {
      return units.filter((unit) => !filteredUnits.includes(unit));
    }

    return filteredUnits;
  };

  /**
   * Helper function to calculate annual rent for a year adusjted with cpi.
   * @param units The units for that year with valid contracts
   * @param annualYear Year to calculate annual rent. Value is added to current year to find the year
   * @param withCPI Calculate total annual revenue with or without cpi
   * @returns
   */
  static getTotalAnnualRent = (
    units: IApiUnit[],
    annualCPIs: annualCalculations,
    currentYear: number,
    annualYear: number,
    withCPI: boolean
  ): number => {
    if (units) {
      const annualYearAsYear = currentYear + annualYear;
      let totalAnnualRent = 0;
      for (let i = 0; i < units.length; i++) {
        const unit = units[i];
        //if (!unit.archived) { TODO: Add when added
        let annualRent = 0;
        const incomeData = unit.fields?.incomeData;
        if (incomeData) {
          annualRent = calculateTotalAnnualRent([incomeData]);
          if (unit.fields?.contractStartDate?.value && withCPI) {
            const cpi = 1 + (annualCPIs[annualYearAsYear]?.cpi ?? 0) / 100;

            let yearDiff = currentYear + annualYear - new Date(unit.fields.contractStartDate.value).getFullYear();
            if (yearDiff > 0) {
              if (yearDiff > annualYear) {
                yearDiff = annualYear;
              }
              annualRent = annualRent * Math.pow(cpi, yearDiff);
            }
          }
          totalAnnualRent += annualRent;
        }
        // }
      }
      return totalAnnualRent;
    } else {
      return 0;
    }
  };

  /**
   * Helper function to calculate annual rent for a year adusjted with cpi.
   * @param unit The unit for that year with valid contracts
   * @param annualYear Year to calculate annual rent. Value is added to current year to find the year
   * @param withCPI Calculate total annual revenue with or without cpi
   * @returns
   */
  static getAnnualRentForUnit = (unit: IApiUnit, annualCPI: annualCalculations, annualYear: number, withCPI: boolean): number => {
    let annualRent = 0;
    const incomeData = unit.fields?.incomeData;
    if (incomeData) {
      annualRent = calculateTotalAnnualRent([incomeData]);
      if (unit.fields?.contractStartDate?.value && withCPI) {
        const cpi = 1 + (annualCPI[annualYear]?.cpi ?? 0) / 100;

        let yearDiff = annualYear - new Date(unit.fields.contractStartDate.value).getFullYear();
        if (yearDiff > 0) {
          if (yearDiff > annualYear) {
            yearDiff = annualYear;
          }
          annualRent = annualRent * Math.pow(cpi, yearDiff);
        }
      }
    }
    return annualRent;
  };

  /**
   * Calculate WAULT after units have been loaded.
   * f(x): (for every unit => rentPerDay*remainingXContractDays)/totalAnnualRent
   */
  static getWault = (landlordUnits: UnitData[][], currentYear: number): number => {
    const units: UnitData[] = [...landlordUnits.flat()];
    const filteredUnits = units.filter((u) => {
      if (u.contractStartDate && u.contractEndDate) {
        return (
          u.contractStartDate.value &&
          u.contractStartDate.value?.getFullYear() <= currentYear &&
          u.contractEndDate.value &&
          u.contractEndDate.value.getFullYear() > currentYear
        );
      } else {
        return false;
      }
    });

    if (filteredUnits.length > 0) {
      const am =
        filteredUnits.reduce((total, unit) => total + unit.getRemainingContractSum(), 0) /
        filteredUnits.reduce((total, units) => total + units.totalRent(), 0);

      return am;
    } else {
      return 0;
    }
  };

  static getApiUnitsWault = (units: IApiUnit[], currentYear: number): number => {
    const filteredUnits = units.filter((u) => {
      if (u.fields?.contractStartDate && u.fields?.contractEndDate) {
        return (
          u.fields.contractStartDate.value &&
          new Date(u.fields.contractStartDate.value).getFullYear() <= currentYear &&
          u.fields.contractEndDate.value &&
          new Date(u.fields.contractEndDate.value).getFullYear() > currentYear
        );
      } else {
        return false;
      }
    });

    if (filteredUnits.length > 0) {
      const am =
        filteredUnits.reduce(
          (total, unit) => total + calculateRemainingContractValue(unit.fields?.incomeData ?? undefined, unit.fields?.contractEndDate?.value),
          0
        ) / filteredUnits.reduce((total, unit) => total + calculateTotalAnnualRent(unit.fields?.incomeData ? [unit.fields.incomeData] : []), 0);

      return am;
    } else {
      return 0;
    }
  };

  /**
   * Calculate CPI range to text
   */
  static getCpiRange = (cpis: annualCalculations): string => {
    const cpiValues = Object.values(cpis);
    const min = Math.min(...cpiValues.map((cpi) => cpi.cpi));
    const max = Math.max(...cpiValues.map((cpi) => cpi.cpi));
    return `${min.toFixed(1)}% - ${max.toFixed(1)}%`;
  };
}

